import React, { useState, useEffect } from 'react';
import { Button, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { Search, FilterX } from "lucide-react";
 
const SearchFilter = ({
  searchValue,
  setSearchValue,
  suggestions,
  showSuggestions,
  setShowSuggestions,
  onSearch,
  startDate,
  setStartDate,
  endDate,
  setEndDate
}) => {
  const [localValue, setLocalValue] = useState(searchValue || "");
 
  // Sync with parent when searchValue changes externally
  useEffect(() => {
    setLocalValue(searchValue || "");
  }, [searchValue]);
 
  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = () => {
      setShowSuggestions?.(false);
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [setShowSuggestions]);
 
  // ✅ ONLY trigger search on Enter key or button click
  const handleSearch = () => {
    if (localValue.trim() !== "") {
      setSearchValue(localValue);
      if (onSearch) {
        onSearch(); // Parent handles the search
      }
      setShowSuggestions?.(false);
    }
  };
 
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch(); // ✅ Search only on Enter
    }
  };
 
  return (
    <Row className="mb-4 justify-content-center">
      <Col lg={6} md={8} sm={12} className="ms-lg-4">
        <div className="mobile-search-wrapper" style={{ position: "relative" }}>
         
          <InputGroup
            className="mobile-search-input-group border rounded-pill bg-white"
            style={{
              overflow: 'hidden',
              borderColor: 'rgba(209, 213, 219, 1)'
            }}
          >
            <InputGroup.Text style={{
              border: 'none',
              backgroundColor: 'transparent',
              paddingRight: '8px'
            }}>
              <Search size={18} color="#6c757d" />
            </InputGroup.Text>
 
            <Form.Control
              placeholder="Search using keywords"
              aria-label="Search tenders"
              value={localValue}
              onChange={(e) => {
                e.stopPropagation();
                const newVal = e.target.value;
                setLocalValue(newVal);
               
                // ✅ Clear search when empty (but don't trigger search)
                if (newVal === "") {
                  setSearchValue("");
                  // Optional: Clear results immediately when empty
                  if (onSearch) onSearch();
                }
              }}
              onKeyDown={handleKeyDown} // ✅ Only Enter triggers search
              style={{ border: 'none', boxShadow: 'none' }}
            />
 
            {/* Desktop Search Button - Triggers search */}
            <Button
              className="desktop-search-btn"
              variant="primary"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleSearch(); // ✅ Search on button click
              }}
              style={{
                width: '44px',
                height: '44px',
                padding: 0,
                border: 'none',
                borderRadius: '50%',
                backgroundColor: '#2F7F8F',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '4px',
              }}
            >
              <Search size={18} color="#fff" />
            </Button>
          </InputGroup>
 
          {/* Mobile Search Button - Triggers search */}
          <Button
            className="mobile-search-btn d-lg-none w-100 mt-2 fw-bold"
            type="button"
            onClick={handleSearch} // ✅ Search on button click
            style={{ height: '48px' }}
          >
            <Search size={18} className="me-2" /> SEARCH
          </Button>
 
          {/* Suggestions */}
          {showSuggestions && suggestions?.length > 0 && (
            <div className="suggestion-box">
              {suggestions.map((item, index) => (
                <div
                  key={index}
                  className="suggestion-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLocalValue(item);
                    setSearchValue(item);
                    setShowSuggestions?.(false);
                    // ✅ Auto-search when suggestion is clicked
                    if (onSearch) onSearch();
                  }}
                >
                  {item}
                </div>
              ))}
            </div>
          )}
 
        </div>
      </Col>
 
      {/* Date Filters (Conditional) */}
      {startDate !== undefined && (
        <Col lg={4} md={4} sm={12} className="d-flex align-items-center gap-2 mt-3 mt-md-0 ps-md-3">
          <div className="d-flex flex-column" style={{flex: 1}}>
            <span className="ms-2 mb-1 fw-semibold text-secondary" style={{fontSize: '12px',marginTop:'-20px'}}>Start Date</span>
            <Form.Control
              type="date"
              value={startDate || ''}
              onChange={(e) => setStartDate && setStartDate(e.target.value)}
              className="border text-secondary bg-white shadow-sm"
              style={{borderRadius: '30px', padding: '10px 15px', fontSize: '13px'}}
            />
          </div>
          <div className="d-flex flex-column" style={{flex: 1}}>
            <span className="ms-2 mb-1 fw-semibold text-secondary" style={{fontSize: '12px',marginTop:'-20px'}}>End Date</span>
            <Form.Control
              type="date"
              value={endDate || ''}
              onChange={(e) => setEndDate && setEndDate(e.target.value)}
              className="border text-secondary bg-white shadow-sm"
              style={{borderRadius: '30px', padding: '10px 15px', fontSize: '13px'}}
            />
          </div>
         
          {(startDate || endDate) && (
            <div className="d-flex align-items-center justify-content-center ms-2" style={{ marginTop: '22px' }}>
              <Button
                variant="light"
                className="p-0 d-flex align-items-center gap-2 shadow-none"
                style={{ background: 'transparent', border: 'none', color: '#333' }}
                onClick={() => {
                  if (setStartDate) setStartDate("");
                  if (setEndDate) setEndDate("");
                }}
                title="Reset Date Filters"
              >
                <img src="/images/filter.svg" alt="Reset" style={{ width: '16px', height: '16px' }} />
                <span style={{ fontWeight: 400, fontSize: '14px', whiteSpace: 'nowrap' }}>Reset Filters</span>
              </Button>
            </div>
          )}
        </Col>
      )}
    </Row>
  );
};
 
export default SearchFilter;
 